<!-- Which part of the clip becomes the animation.
  --
  -- A filmstrip with a handle at each end, the shape every phone uses for this
  -- and the one the request pointed at. It replaces a rule rather than adding
  -- a control: the page used to take the first ten seconds and tell you so
  -- afterwards, which is a decision made on somebody's behalf and reported as
  -- a fact.
  --
  -- THE SELECTION MAY BE LONGER THAN THE CAP. What costs bytes is the number
  -- of frames, not how much of the source they came from -- measured, 100
  -- frames at 480x270 is about 7.6 MB whether it spans ten seconds or sixty.
  -- So a wide selection is sampled across the whole span and played at the
  -- chosen rate, which comes out as a speed-up, and the line under the strip
  -- says so before anything is converted. Nothing is discarded unless the
  -- visitor discards it.
  --
  -- The thumbnails are <img> with data URLs rather than one drawn canvas: the
  -- strip has to survive being re-laid-out at any width, and letting the
  -- browser scale ten small JPEGs is less code than redrawing on resize.
  -->
<template>
    <div id="video-trimmer" class="rounded-2xl border border-rule bg-raised p-5 flex flex-col gap-4">

        <div class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('video.chooseRange') }}</span>
            <span v-if="uiStore.videoName" class="text-[15px] font-bold break-all leading-snug">{{uiStore.videoName}}</span>
        </div>

        <!-- The strip. `touch-action: none` so a drag on a phone moves the
          -- handle instead of scrolling the page away underneath it. -->
        <div ref="strip" data-video-strip
             class="relative select-none rounded-lg overflow-hidden border border-rule"
             style="touch-action: none; cursor: pointer"
             @pointerdown="onStripDown">

            <div class="flex w-full" :style="`height: ${stripHeight}px`" aria-hidden="true">
                <img v-for="(src, i) in uiStore.videoStrip" :key="i" :src="src" alt=""
                     class="h-full min-w-0 flex-1 object-cover" draggable="false" />
            </div>

            <!-- Outside the selection, dimmed. Two panels rather than one
              -- box-shadow, so the dimming is the same on both sides whatever
              -- the selection is.
              --
              -- black/55 AND NOT paper/70. A palette token is `var(--paper)`
              -- with no <alpha-value> in it, so Tailwind cannot build an
              -- opacity variant from one and `bg-paper/70` generates NOTHING
              -- -- these two panels had no background at all and dimmed
              -- nothing. Only the built-in colours take a /NN modifier. Same
              -- silent-failure class as the `text-warn` that was never a
              -- token. -->
            <div class="absolute inset-y-0 left-0 bg-black/50 pointer-events-none"
                 :style="`width: ${leftPct}%`"></div>
            <div class="absolute inset-y-0 right-0 bg-black/50 pointer-events-none"
                 :style="`width: ${100 - rightPct}%`"></div>

            <div class="absolute inset-y-0 border-y-2 border-brand pointer-events-none"
                 :style="`left: ${leftPct}%; width: ${rightPct - leftPct}%`"></div>

            <!-- Handles. Wide enough to hit with a thumb: the visible bar is
              -- 6px and the target around it is 22. -->
            <button v-for="side in ['start', 'end']" :key="side"
                    type="button"
                    :data-video-handle="side"
                    class="absolute inset-y-0 flex w-[22px] items-center justify-center"
                    style="touch-action: none; cursor: ew-resize"
                    :style="`left: calc(${side === 'start' ? leftPct : rightPct}% - 11px)`"
                    :aria-label="side === 'start' ? t('video.rangeStart') : t('video.rangeEnd')"
                    @pointerdown.stop="onHandleDown(side, $event)">
                <span class="h-full w-[6px] rounded-full bg-brand shadow"></span>
            </button>
        </div>

        <!-- What you are actually going to get. Both numbers, because they are
          -- different numbers the moment the selection passes the cap, and the
          -- speed-up is the surprising one. -->
        <div class="flex flex-col gap-1">
            <p id="video-range" class="text-[15px] tabular-nums">
                <span class="font-bold">{{ clock(plan.span) }}</span>
                {{ ' ' }}<span class="text-ink-soft">{{ t('video.selected') }}</span>
            </p>
            <p id="video-outcome" class="text-[15px] leading-snug"
               :class="plan.sped ? 'text-ink' : 'text-ink-soft'">
                <template v-if="plan.sped">
                    <strong>{{ t('video.spedUp', { seconds: clock(plan.seconds), speed: speedLabel }) }}</strong>
                    {{ ' ' }}{{ t('video.spedUpHow') }}
                </template>
                <template v-else>
                    {{ t('video.willBe', { seconds: clock(plan.seconds), frames: plan.times.length }) }}
                </template>
            </p>
        </div>

        <div class="flex flex-wrap gap-3">
            <button type="button" data-video-convert
                    class="btn btn-primary rounded-xl border-0 bg-brand-fill text-on-brand hover:bg-brand-fill-over"
                    @click="$emit('convert')">
                {{ t('video.makeIt') }}
            </button>
            <button type="button" data-video-cancel
                    class="btn btn-ghost rounded-xl" @click="$emit('cancel')">
                {{ t('video.chooseAnother') }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "./../store/uiElements"
import { planFrames, STRIP_HEIGHT, MAX_SECONDS } from "./../lib/videoFrames"

const uiStore = useUiElements()
const { t } = useI18n()
defineEmits(["convert", "cancel"])

const strip = ref(null)
const stripHeight = STRIP_HEIGHT

/* Read out of the store on every frame and written straight back during a
 * drag. NO LOCAL COPY, for the reason the caption rectangles record: a copy
 * needs pushing one way and pulling the other, and the thing on screen ends up
 * following the copy rather than the value about to be used. */
const plan = computed(() => planFrames(
    uiStore.videoDuration, uiStore.videoFps, MAX_SECONDS,
    uiStore.videoStart, uiStore.videoEnd))

const pct = (t_) => {
    const d = uiStore.videoDuration
    return d > 0 ? Math.min(100, Math.max(0, (t_ / d) * 100)) : 0
}
const leftPct = computed(() => pct(uiStore.videoStart))
const rightPct = computed(() => pct(uiStore.videoEnd))

/* 1.3x, not 1.2999999x. One decimal, and no decimal at all when it is whole:
 * "3x faster" reads as a fact and "3.0x faster" reads as a measurement. */
const speedLabel = computed(() => {
    const s = plan.value.speed
    return (Math.abs(s - Math.round(s)) < 0.05 ? Math.round(s) : s.toFixed(1)) + "×"
})

/* m:ss, because 0:07 is how long a video is and 7.0s is how long a benchmark
 * takes. Seconds are rounded, so a 9.6-second span reads 0:10 -- which is
 * what the visitor will see the animation run for. */
function clock(seconds) {
    const total = Math.max(0, Math.round(Number(seconds) || 0))
    return Math.floor(total / 60) + ":" + String(total % 60).padStart(2, "0")
}

/* The shortest selection worth having. Below about a second there is nothing
 * to animate, and at 5 fps a quarter-second span is one frame. */
const MIN_SPAN = 0.5

function timeAt(clientX) {
    const el = strip.value
    if (!el) return 0
    const r = el.getBoundingClientRect()
    if (!r.width) return 0
    const share = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return share * uiStore.videoDuration
}

function onHandleDown(side, ev) {
    /* setPointerCapture throws NotFoundError for a synthetic pointerId, which
     * is exactly what a test dispatches. Capture is a nicety -- the window
     * listeners below fire either way -- so it is attempted and ignored. Same
     * guard the caption rectangles needed. */
    try { ev.target.setPointerCapture(ev.pointerId) } catch (e) { /* synthetic */ }

    const move = (e) => {
        const at = timeAt(e.clientX)
        if (side === "start") {
            uiStore.videoStart = Math.min(at, uiStore.videoEnd - MIN_SPAN)
        } else {
            uiStore.videoEnd = Math.max(at, uiStore.videoStart + MIN_SPAN)
        }
    }
    const up = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
        window.removeEventListener("pointercancel", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", up)
    move(ev)
}

/* Tapping the strip moves the NEARER handle. Dragging the window itself was
 * the other option and is worse on a phone: the two handles are 22px wide and
 * the gap between them is most of the strip, so most taps would land on the
 * window and mean nothing. */
function onStripDown(ev) {
    const at = timeAt(ev.clientX)
    const side = Math.abs(at - uiStore.videoStart) <= Math.abs(at - uiStore.videoEnd)
        ? "start" : "end"
    onHandleDown(side, ev)
}
</script>
